import React, { useEffect } from "react";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  enablePaymentProvider,
  disablePaymentProvider,
  getGoCardlessSettings,
  selectGoCardlessSettings,
  updateGoCardlessSettings,
  authoriseGoCardlessMerchant,
  revokeGoCardlessAccessToken,
  PaymentProviderComponentProps,
} from "../components/PaymentProvidersSlice";
import { useSelector } from "react-redux";
import { GoCardlessSettingsModel } from "services/api/cplapi/CPLAPI";
import { PaymentProviders } from "global/enums";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import PaymentProvider, { PaymentProviderSettingsProps } from "../components/PaymentProvider";

const validationSchema = Yup.object().shape({
  paymentDescription: Yup.string().required("Payment description is required"),
});

const GoCardlessSettings: React.FC<PaymentProviderSettingsProps> = ({ allowEditing }: PaymentProviderSettingsProps) => {
  const thunkDispatch = useThunkDispatch();
  const goCardlessSettings = useSelector(selectGoCardlessSettings);

  useEffect(() => {
    thunkDispatch(getGoCardlessSettings());
  }, [thunkDispatch]);

  const handleSubmit = (values: GoCardlessSettingsModel) => {
    thunkDispatch(updateGoCardlessSettings(values));
  };
  return (
    <Formik
      initialValues={goCardlessSettings}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (allowEditing) handleSubmit(values);
      }}
      enableReinitialize
    >
      <Form>
        <div className="row mb-6">
          <label className="form-label">Email Address</label>
          <Field
            data-test-id="go-cardless-settings-form__email-address-input"
            className="form-control form-control-solid"
            name="emailAddress"
            disabled
          />
        </div>
        <div className="row mb-6">
          <label className="form-label">Payment Description</label>
          <Field
            data-test-id="go-cardless-settings-form__payment-description-input"
            className="form-control form-control-solid"
            name="paymentDescription"
            disabled={!allowEditing}
          />
        </div>
        {allowEditing && (
          <div className="row mb-6">
            <button data-test-id="go-cardless-settings-form__save-button" className="btn btn-success" type="submit">
              Save
            </button>
          </div>
        )}
      </Form>
    </Formik>
  );
};

const GoCardless: React.FC<PaymentProviderComponentProps> = ({ displayName }: PaymentProviderComponentProps) => {
  const thunkDispatch = useThunkDispatch();

  const setPaymentProviderEnabled = (enable: boolean) => {
    if (enable) {
      thunkDispatch(enablePaymentProvider(PaymentProviders.GoCardless));
    } else {
      thunkDispatch(disablePaymentProvider());
    }
  };

  const handleAuthoriseMerchant = async () => {
    const authoriseResponse = await thunkDispatch(authoriseGoCardlessMerchant()).unwrap();
    if (authoriseResponse.redirectURL !== undefined) {
      const newWindow = window.open(authoriseResponse.redirectURL, "_blank");
      newWindow?.focus();
    }
  };

  const removePaymentProvider = () => {
    thunkDispatch(revokeGoCardlessAccessToken());
  };

  return (
    <PaymentProvider
      paymentProviderDisplayName={displayName}
      paymentProviderID={PaymentProviders.GoCardless}
      setPaymentProviderEnabled={setPaymentProviderEnabled}
      removePaymentProvider={removePaymentProvider}
      PaymentProviderSettings={GoCardlessSettings}
      PaymentProviderSetup={() => (
        <div className="row mb-6">
          <div className="col-lg-8">
            <h4>GoCardless Setup</h4>
            <p>Connect your GoCardless account to collect bank payments on the CPL Client Portal</p>
          </div>
          <div className="col-lg-4">
            <button
              data-test-id="go-cardless-tab__connect-go-cardless-button"
              className="btn btn-success"
              type="button"
              onClick={handleAuthoriseMerchant}
            >
              Connect GoCardless
            </button>
          </div>
        </div>
      )}
    />
  );
};

export default GoCardless;
